/**
 * 🎁 推薦計劃（任務 I；2026-09-08 Captain 拍板經銷商模式）：
 * 經銷商由後台手動生成管理；客人 /?r=CODE 入 landing → 對話 → lead（自動通知經銷商）；
 * 成交由管理員喺呢頁人手標記 → 按 %／定額計回饋 → WhatsApp 通知經銷商（積極性引擎）。
 * 回饋只記錄＋通知，實際派發由 Captain 處理。
 */
import { useCallback, useEffect, useState } from "react";
import ReferralV2Section from "./ReferralV2Section";
import {
  getReferralOverview,
  createReferrer,
  updateReferrer,
  deleteReferrer,
  convertReferralLead,
  getReferralSplits,
  adminGovTeam,
  type ReferrerView,
  type ReferralLeadView,
  type ReferralSplitView,
} from "../lib/api";

/** Phase 1（G-01）：兩級制角色判定——admin 要一眼分清 Master／團隊長／直屬／小代理 */
function tierOf(r: ReferrerView): { label: string; cls: string } {
  if (r.is_org === 1) return { label: "Master", cls: "bg-amber-500/20 text-amber-300" };
  if (r.parent_id) return { label: "小代理", cls: "bg-sky-500/15 text-sky-300" };
  if ((r.subs || 0) > 0) return { label: "團隊長", cls: "bg-emerald-500/15 text-emerald-300" };
  return { label: "直屬", cls: "bg-slate-700/60 text-slate-300" };
}

const fmtDate = (ms: number | null) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** 6 位隨機推薦碼（脫敏：唔含任何身份資訊；去除易混淆字元 I/L/O/0/1） */
function genCode(): string {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let s = "";
  for (let i = 0; i < 6; i++) s += chars[bytes[i] % chars.length];
  return s;
}

export default function ReferralsPage() {
  const [referrers, setReferrers] = useState<ReferrerView[]>([]);
  const [leads, setLeads] = useState<ReferralLeadView[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ReferrerView | null>(null);
  const [form, setForm] = useState({ name: "", phone: "", code: "", reward_mode: "percentage", reward_value: 15, note: "", sub_pct: "" });
  /* G-02：逐單拆帳明細——全系統唯一記錄「Direct 與 Sub 各自輸入的比例」的地方 */
  const [splits, setSplits] = useState<ReferralSplitView[]>([]);
  const [splitsLoading, setSplitsLoading] = useState(false);
  const [splitsErr, setSplitsErr] = useState("");
  /* P14（2026-10-15）：平台兩級治理——admin 直接介入改掛／覆寫比例（與夥伴後台共用同一交易） */
  const [govTarget, setGovTarget] = useState<ReferrerView | null>(null);
  const [govNewParent, setGovNewParent] = useState("");
  const [govPct, setGovPct] = useState("");
  const [govBusy, setGovBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferralOverview();
      setReferrers(data.referrers);
      setLeads(data.leads);
    } catch (e) {
      setMsg("載入失敗：" + String(e).slice(0, 120));
    }
    setLoading(false);
  }, []);

  const loadSplits = useCallback(async () => {
    setSplitsLoading(true);
    setSplitsErr("");
    try {
      setSplits(await getReferralSplits());
    } catch (e) {
      setSplitsErr(String(e).slice(0, 160));
    }
    setSplitsLoading(false);
  }, []);

  /* P14 治理執行：平台介入屬高風險動作——一律二次確認；完成後重載列表與拆帳明細 */
  const runGov = useCallback(
    async (
      action: "team-suspend" | "team-remove" | "team-reassign" | "team-sub-pct",
      body: Record<string, unknown>,
      label: string,
      confirmText: string
    ) => {
      if (!govTarget) return;
      if (!window.confirm(confirmText)) return;
      setGovBusy(true);
      setMsg("");
      try {
        const data = await adminGovTeam(action, body);
        setMsg(`✅ ${label}完成：${JSON.stringify(data)}`);
        setGovTarget(null);
        setGovNewParent("");
        setGovPct("");
        await load();
      } catch (e) {
        setMsg(`❌ ${label}失敗：` + String(e).slice(0, 200));
      }
      setGovBusy(false);
    },
    [govTarget, load]
  );

  useEffect(() => {
    load();
    loadSplits();
  }, [load, loadSplits]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", phone: "852", code: genCode(), reward_mode: "percentage", reward_value: 15, note: "", sub_pct: "" });
    setShowForm(true);
  };

  const openEdit = (r: ReferrerView) => {
    setEditing(r);
    setForm({
      name: r.name,
      phone: r.phone,
      code: r.code,
      reward_mode: r.reward_mode,
      reward_value: r.reward_value,
      note: r.note || "",
      sub_pct: r.sub_pct === null || r.sub_pct === undefined ? "" : String(r.sub_pct),
    });
    setShowForm(true);
  };

  const saveForm = async () => {
    setMsg("");
    try {
      if (editing) {
        await updateReferrer(editing.id, { ...form, sub_pct: form.sub_pct === "" ? null : Number(form.sub_pct) });
        setMsg("✅ 已更新");
      } else {
        const r = await createReferrer(form);
        setMsg(`✅ 已建立——經銷商專屬連結：${r.link}（已複製到剪貼簿的話直接轉發俾佢）`);
        try {
          navigator.clipboard.writeText(r.link);
        } catch {}
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setMsg("❌ " + String(e).slice(0, 160));
    }
  };

  const toggleActive = async (r: ReferrerView) => {
    if (r.active) {
      if (!window.confirm(`確定停用「${r.name}」？\n\n若其為團隊長，旗下小代理會按 D-1 升為直屬 Agent。`)) return;
      await adminGovTeam("team-suspend", { id: r.id });
    } else {
      await updateReferrer(r.id, { active: 1 });
    }
    await load();
  };

  const markConverted = async (lead: ReferralLeadView) => {
    const input = window.prompt(
      `標記成交——客人 ${lead.phone}\n輸入成交金額（HKD 整數，例如 199 或 499）：`,
      "199"
    );
    if (!input) return;
    const amount = Math.round(Number(input));
    if (!Number.isFinite(amount) || amount <= 0) {
      window.alert("金額無效");
      return;
    }
    try {
      const r = await convertReferralLead(lead.id, amount);
      window.alert(
        `✅ 已標記成交＋通知經銷商 ${r.notified}。\n回饋金額：HK$${r.reward_amount}（按 ${lead.referrer_name} 嘅計劃計算——實際派發你自行處理）`
      );
      await load();
    } catch (e) {
      window.alert("❌ " + String(e).slice(0, 160));
    }
  };

  const removeReferrer = async (r: ReferrerView) => {
    if (!window.confirm(`確定刪除經銷商「${r.name}」？\n其查詢紀錄／成交／回饋紀錄會一併刪除（不可復原）。`)) return;
    try {
      await deleteReferrer(r.id);
      setMsg(`✅ 已刪除 ${r.name}（${r.code}）連同其全部紀錄`);
      await load();
    } catch (e) {
      setMsg("❌ " + String(e).slice(0, 160));
    }
  };

  const copyLink = (code: string) => {
    navigator.clipboard.writeText(`https://wtsaibot.com/?r=${code}`);
    setMsg(`✅ 已複製 ${code} 嘅專屬連結`);
  };

  return (
    <div>
      <ReferralV2Section />
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-slate-100">🎁 推薦計劃</h1>
        <button
          onClick={load}
          className="px-3 py-1.5 rounded-lg text-xs border border-slate-700 text-slate-300 hover:border-emerald-500"
        >
          🔄 重新載入
        </button>
      </div>
      {/* v60 兩級代理架構說明卡（2026-10-13 Captain：admin 要一眼理解架構與各 stakeholder 體驗） */}
      <div className="mb-4 p-4 rounded-xl border border-amber-500/30 bg-amber-500/[.06] text-xs text-slate-300 leading-relaxed">
        <div className="font-bold text-amber-300 mb-2">🪜 兩級代理制架構總覽（v60）</div>
        <div className="font-mono text-[11px] text-slate-400 mb-3 whitespace-pre">{`經銷商 Master（EiD）
 ├─ 直屬 Agent（parent 空）──自己帶客：佣金 20% 全額
 └─ Direct Agent（parent 空、有下線）──招了小代理即自動成為團隊長
      └─ 小代理 Sub（parent＝Direct）──三層封頂，不可再招`}</div>
        <div className="grid md:grid-cols-4 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-slate-900/70 border border-slate-700/60"><b className="text-slate-100">直屬 Agent</b><br />後台見：連結／QR＋旗下小代理招募區。佣金：自帶客 20% 全額。</div>
          <div className="p-2 rounded-lg bg-slate-900/70 border border-slate-700/60"><b className="text-slate-100">Direct 團隊長</b><br />同上＋團隊表＋「團隊拆分 %」自調＋應付小代理合計。自己帶客照樣 20% 全額。</div>
          <div className="p-2 rounded-lg bg-slate-900/70 border border-slate-700/60"><b className="text-slate-100">小代理 Sub</b><br />後台見連結／QR＋「您的團隊長」卡（無招募——封頂）。團隊份額由團隊長按月支付。</div>
          <div className="p-2 rounded-lg bg-slate-900/70 border border-slate-700/60"><b className="text-slate-100">Master 經銷商</b><br />夥伴後台：宣傳員卡＋旗下 Agent 表（含小代理數＋拆分%）。對賬全渠道。</div>
        </div>
        <div>佣金觸發單一定義＝<b className="text-emerald-400">正式實付</b>（9.9 試用費不計佣、免費開戶已退役）。帶 <span className="text-amber-300">🟡 小代理標籤</span>的行＝掛靠在上一層團隊長名下；「團隊拆分 %」＝該團隊長讓渡給小代理的總佣金比例（預設 40%，0-80 可調）。實際派發：平台↔經銷商直接結算，團隊長對小代理按月人手支付。</div>
      </div>
      {msg && <div className="mb-4 p-3 bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-300 break-all">{msg}</div>}

      {/* ── 經銷商 ── */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-200">🤝 經銷商（{referrers.length}）</h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600/20 border border-emerald-500 text-emerald-300 hover:bg-emerald-600/30"
        >
          ＋ 新增經銷商
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-900 border border-emerald-800/40 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">
              經銷商名稱 *
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="例如：Alex Kitchen"
                className="mt-1 w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100"
              />
            </label>
            <label className="text-xs text-slate-400">
              通知 WhatsApp 號碼 *（852…）
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 font-mono"
              />
            </label>
            <label className="text-xs text-slate-400">
              推薦碼 *（6 位隨機英數——脫敏：碼唔含任何經銷商身份資訊）
              <div className="mt-1 flex gap-2">
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="w-36 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 font-mono tracking-widest"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, code: genCode() })}
                  className="px-3 py-2 rounded-lg text-xs border border-slate-600 text-slate-300 hover:border-emerald-500 hover:text-emerald-300"
                  title="重新生成隨機碼"
                >
                  🎲 重新生成
                </button>
              </div>
            </label>
            <div className="text-xs text-slate-400">
              回饋模式 *
              <div className="mt-1 flex gap-2">
                <select
                  value={form.reward_mode}
                  onChange={(e) => setForm({ ...form, reward_mode: e.target.value })}
                  className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100"
                >
                  <option value="percentage">成交額 %</option>
                  <option value="fixed">固定 HK$</option>
                </select>
                <input
                  type="number"
                  value={form.reward_value}
                  onChange={(e) => setForm({ ...form, reward_value: Number(e.target.value) })}
                  className="w-28 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100"
                />
                <span className="self-center">{form.reward_mode === "percentage" ? "%" : "HK$"}</span>
              </div>
            </div>
            <label className="text-xs text-slate-400 md:col-span-2">
              團隊拆分 %（小代理佔總佣金的百分比；留空＝預設 40。例：40 即小代理 8%／團隊長 12%）
              <input
                type="number"
                min={0}
                max={80}
                value={form.sub_pct}
                onChange={(e) => setForm({ ...form, sub_pct: e.target.value })}
                placeholder="40"
                className="mt-1 w-32 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100"
              />
            </label>
            <label className="text-xs text-slate-400 md:col-span-2">
              備註
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={saveForm} className="px-4 py-2 rounded-lg text-sm bg-emerald-600/30 border border-emerald-500 text-emerald-300">
              {editing ? "💾 儲存修改" : "✅ 建立經銷商"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm border border-slate-700 text-slate-400">
              取消
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto mb-8">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
            <tr>
              <th className="px-3 py-3">經銷商</th>
              <th className="px-3 py-3">層級</th>
              {/* G-01：此前 sub_pct 只存在於編輯表單，平台無從監控 Direct 讓渡了多少給 Sub */}
              <th className="px-3 py-3 text-center">團隊拆分 %</th>
              <th className="px-3 py-3">推薦碼／連結</th>
              <th className="px-3 py-3">回饋</th>
              <th className="px-3 py-3 text-center">訪客</th>
              <th className="px-3 py-3 text-center">查詢</th>
              <th className="px-3 py-3 text-center">成交</th>
              <th className="px-3 py-3 text-center">累計回饋</th>
              <th className="px-3 py-3">狀態</th>
              <th className="px-3 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">載入中…</td></tr>
            )}
            {!loading && referrers.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">未有經銷商——撳「＋ 新增經銷商」開始</td></tr>
            )}
            {[...referrers].sort((a, b) => {
              /* v60 樹狀鋪排：頂層（直屬／團隊長）先，小代理緊隨其團隊長之後——架構一眼可讀 */
              const key = (x: typeof a) => x.parent_id ? `s:${x.parent_name || x.parent_id}` : `t:${x.name}`;
              const ka = key(a), kb = key(b);
              if (ka !== kb) return ka < kb ? -1 : 1;
              if (a.parent_id !== b.parent_id) return (a.parent_id || 0) - (b.parent_id || 0);
              return b.created_at - a.created_at;
            }).map((r) => (
              <tr key={r.id} className={`bg-slate-900/50 border-b border-slate-800 ${r.active ? "" : "opacity-50"}`}>
                <td className="px-3 py-3">
                  <div className="text-slate-200 font-medium">{r.parent_name ? <span className="text-slate-500 mr-1">└</span> : null}{r.name}{r.parent_name ? <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">小代理 · {r.parent_name}</span> : null}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{r.phone}</div>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded ${tierOf(r).cls}`}>{tierOf(r).label}</span>
                  {!r.parent_id && (r.subs || 0) > 0 ? <div className="text-[11px] text-slate-500 mt-1">旗下 {r.subs}</div> : null}
                </td>
                <td className="px-3 py-3 text-center">
                  {r.parent_id ? (
                    <span className="text-[11px] text-slate-600">—</span>
                  ) : (
                    <span className="text-amber-300 font-bold">{r.sub_pct === null || r.sub_pct === undefined ? "40" : r.sub_pct}<span className="text-slate-500 font-normal">%</span>{r.sub_pct === null || r.sub_pct === undefined ? <div className="text-[10px] text-slate-600">預設</div> : null}</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => copyLink(r.code)}
                    className="font-mono text-xs text-sky-300 hover:text-sky-200"
                    title="點擊複製專屬連結"
                  >
                    /?r={r.code} 📋
                  </button>
                </td>
                <td className="px-3 py-3 text-xs text-slate-300">
                  {r.reward_mode === "percentage" ? `${r.reward_value}%` : `HK$${r.reward_value} 定額`}
                </td>
                <td className="px-3 py-3 text-center text-slate-400">{r.visits}</td>
                <td className="px-3 py-3 text-center text-slate-300">{r.leads}</td>
                <td className="px-3 py-3 text-center text-emerald-300 font-bold">{r.converted}</td>
                <td className="px-3 py-3 text-center text-amber-300 font-bold">HK${Math.round((Number(r.rewards_total) || 0) * 100) / 100}</td>
                <td className="px-3 py-3">
                  {r.status === "active" ? (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">已審核</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700/60 text-slate-400">{r.status || "pending"}</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => openEdit(r)} className="text-sky-400 hover:text-sky-300">編輯</button>
                    <button onClick={() => toggleActive(r)} className={r.active ? "text-rose-400 hover:text-rose-300" : "text-emerald-400 hover:text-emerald-300"}>
                      {r.active ? "停用" : "啟用"}
                    </button>
                    {/* P14：平台兩級治理入口（停用／解掛／改掛／覆寫比例） */}
                    <button onClick={() => setGovTarget(r)} className="text-amber-400 hover:text-amber-300" title="兩級治理：停用／解除掛靠／改掛／覆寫團隊拆分 %">治理</button>
                    <button onClick={() => removeReferrer(r)} className="text-slate-500 hover:text-rose-400">刪除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── P14（2026-10-15）：平台兩級治理面板 ──
          與夥伴後台共用同一交易函式：停用／解掛會連帶改掛旗下小代理（D-1），改掛不可跨經銷商。
          每次操作寫 referrer_link_events（actor_role='admin'）與 referrer_pct_audit（actor='admin:platform'）。 */}
      {govTarget && (
        <div className="mb-8 p-4 rounded-xl bg-amber-500/5 border border-amber-500/30">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-amber-200">
              🛠 兩級治理（平台介入）——{govTarget.name}（{govTarget.code}）· {tierOf(govTarget).label}
              {govTarget.parent_name ? ` · 現掛靠 ${govTarget.parent_name}` : ""}
            </h2>
            <button onClick={() => setGovTarget(null)} className="text-xs text-slate-400 hover:text-slate-200">關閉</button>
          </div>
          <div className="text-[11px] text-slate-500 mb-3">
            高風險動作——每個操作都會二次確認並留痕（事件軌 actor＝admin；比例審計 actor＝admin:platform）。
            停用團隊長會連帶把旗下小代理改掛經銷商（D-1）；改掛不可跨經銷商。<b className="text-slate-400">已成交單沿用凍結快照，不受任何比例變動影響。</b>
          </div>
          <div className="flex flex-wrap items-end gap-3 text-xs">
            <button
              disabled={govBusy}
              onClick={() => runGov("team-suspend", { id: govTarget.id }, "停用", `確定停用「${govTarget.name}」？\n\n若其為團隊長，旗下小代理會一併改掛經銷商（升為直屬 Agent）。`)}
              className="px-3 py-1.5 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40"
            >停用</button>
            <button
              disabled={govBusy}
              onClick={() => runGov(
                "team-remove",
                { id: govTarget.id },
                govTarget.parent_id ? "解除掛靠" : "移除",
                govTarget.parent_id
                  ? `確定解除「${govTarget.name}」與 ${govTarget.parent_name} 的掛靠？\n\n將升為直屬 Agent（保持啟用），佣金權益不變。`
                  : `確定移除「${govTarget.name}」？\n\n（其無上級，等同停用）`
              )}
              className="px-3 py-1.5 rounded bg-slate-700/60 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
            >{govTarget.parent_id ? "解除掛靠" : "移除"}</button>
            <div className="flex items-end gap-2">
              <label className="text-slate-400">
                改掛至推薦碼
                <input
                  value={govNewParent}
                  onChange={(e) => setGovNewParent(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  placeholder="DIRB"
                  className="ml-2 w-32 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-100 font-mono"
                />
              </label>
              <button
                disabled={govBusy || !govNewParent.trim()}
                onClick={() => runGov("team-reassign", { id: govTarget.id, new_parent_code: govNewParent.trim() }, "改掛", `確定把「${govTarget.name}」改掛到 ${govNewParent.trim()}？\n\n新團隊長須屬同一經銷商、本身無上級。`)}
                className="px-3 py-1.5 rounded bg-sky-500/20 text-sky-300 hover:bg-sky-500/30 disabled:opacity-40"
              >改掛</button>
            </div>
            <div className="flex items-end gap-2">
              <label className="text-slate-400">
                覆寫團隊拆分 %
                <input
                  value={govPct}
                  onChange={(e) => setGovPct(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="40"
                  className="ml-2 w-20 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-100"
                />
              </label>
              <button
                disabled={govBusy || govPct === ""}
                onClick={() => runGov("team-sub-pct", { id: govTarget.id, sub_pct: Number(govPct) }, "覆寫比例", `確定把「${govTarget.name}」的團隊拆分 % 覆寫為 ${govPct}？\n\n範圍 0-80；僅對新成交單生效。`)}
                className="px-3 py-1.5 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 disabled:opacity-40"
              >覆寫</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 轉化紀錄 ── */}
      <h2 className="text-lg font-semibold text-slate-200 mb-3">
        📈 轉化紀錄（最近 {leads.length} 筆）——「標記成交」＝輸入金額後自動計回饋＋通知經銷商
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
            <tr>
              <th className="px-3 py-3">時間</th>
              <th className="px-3 py-3">經銷商</th>
              <th className="px-3 py-3">客人號碼</th>
              <th className="px-3 py-3">首句</th>
              <th className="px-3 py-3">狀態</th>
              <th className="px-3 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">載入中…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                未有查詢——客人經 /?r=CODE 入 landing 撳 WhatsApp CTA 開始對話後會喺度出現
              </td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="bg-slate-900/50 border-b border-slate-800">
                <td className="px-3 py-3 text-xs text-slate-400 font-mono">{fmtDate(l.created_at)}</td>
                <td className="px-3 py-3 text-slate-300">{l.referrer_name}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-300">{l.phone}</td>
                <td className="px-3 py-3 text-xs text-slate-400 max-w-xs truncate">{l.first_message || "—"}</td>
                <td className="px-3 py-3">
                  {l.status === "converted" ? (
                    <div>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">✅ 成交</span>
                      <div className="text-[11px] text-slate-500 mt-1">
                        HK${l.deal_amount} → 回饋 HK${l.reward_amount}（{fmtDate(l.converted_at)}）
                      </div>
                    </div>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-500/20 text-sky-400">查詢中</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {l.status === "lead" ? (
                    <button
                      onClick={() => markConverted(l)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600/20 border border-amber-600 text-amber-300 hover:bg-amber-600/30"
                    >
                      💰 標記成交
                    </button>
                  ) : (
                    <span className="text-xs text-slate-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 逐單拆帳明細（G-02）── */}
      <div className="flex items-center justify-between mb-3 mt-8 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-200">🧾 渠道訂單拆帳明細（{splits.length}）</h2>
        <button
          onClick={loadSplits}
          className="px-3 py-1.5 rounded-lg text-xs border border-slate-700 text-slate-300 hover:border-emerald-500"
        >
          🔄 重新載入
        </button>
      </div>
      <div className="mb-3 p-3 rounded-lg border border-slate-700 bg-slate-900/60 text-xs text-slate-400 leading-relaxed">
        資料源＝<code className="text-slate-300">onboarding_orders.split_snapshot</code>：成交一刻由系統凍結，事後調價不影響已成交訂單。
        <b className="text-slate-300">平台不參與 Direct 與 Sub 之間的分錢</b>，但此表完整記錄雙方輸入的比例與金額，作為對帳與爭議裁決依據。
      </div>
      {splitsErr && <div className="mb-3 p-3 rounded-lg border border-rose-800/50 bg-rose-950/30 text-xs text-rose-300">❌ {splitsErr}</div>}
      {splitsLoading && <div className="text-sm text-slate-500 mb-3">載入中…</div>}
      {!splitsLoading && splits.length === 0 && !splitsErr && (
        <div className="text-sm text-slate-500 mb-3">暫無渠道訂單——客人經專屬連結付款開通後，拆帳快照會在此出現。</div>
      )}
      {splits.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
              <tr>
                <th className="px-3 py-3">時間</th>
                <th className="px-3 py-3">訂單</th>
                <th className="px-3 py-3">方案</th>
                <th className="px-3 py-3 text-right">實付</th>
                <th className="px-3 py-3">歸屬 Agent</th>
                <th className="px-3 py-3">上級</th>
                <th className="px-3 py-3 text-right">總佣</th>
                <th className="px-3 py-3 text-center">Sub %</th>
                <th className="px-3 py-3 text-right">Sub HK$</th>
                <th className="px-3 py-3 text-right">Direct HK$</th>
                <th className="px-3 py-3 text-right">org HK$</th>
                <th className="px-3 py-3 text-right">AIBot HK$</th>
                <th className="px-3 py-3">狀態</th>
              </tr>
            </thead>
            <tbody>
              {splits.map((s) => {
                const sp = s.split;
                return (
                  <tr key={s.order_id} className="bg-slate-900/50 border-b border-slate-800">
                    <td className="px-3 py-3 text-xs text-slate-400 font-mono">{fmtDate(s.created_at)}</td>
                    <td className="px-3 py-3 text-xs font-mono text-slate-300">{s.order_id}</td>
                    <td className="px-3 py-3 text-xs text-slate-400">{s.plan || "—"}</td>
                    <td className="px-3 py-3 text-right text-slate-200">{sp?.gross ?? s.amount ?? "—"}</td>
                    <td className="px-3 py-3 text-slate-300">{s.referrer_name || s.ref_code || "—"}</td>
                    <td className="px-3 py-3 text-xs text-slate-400">{s.parent_name || "—"}</td>
                    <td className="px-3 py-3 text-right text-emerald-300">{sp?.agent_hkd ?? "—"}</td>
                    <td className="px-3 py-3 text-center text-amber-300">{sp?.sub_pct === undefined ? "—" : `${sp.sub_pct}%`}</td>
                    <td className="px-3 py-3 text-right text-amber-300">{sp?.sub_hkd ?? "—"}</td>
                    <td className="px-3 py-3 text-right text-emerald-300">{sp?.direct_hkd ?? "—"}</td>
                    <td className="px-3 py-3 text-right text-slate-400">{sp?.org_hkd ?? "—"}</td>
                    <td className="px-3 py-3 text-right text-slate-400">{sp?.aibot_hkd ?? "—"}</td>
                    <td className="px-3 py-3">
                      {s.unfrozen ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-rose-500/20 text-rose-400" title="有推薦碼且有金額，但拆帳快照未凍結——對數缺口">⚠ 未凍結</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">✅ 已凍結</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
